import {memo, useCallback, useRef, useState, type ButtonHTMLAttributes, type Ref, type RefObject} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Button, CounterLabel, IconButton, SegmentedControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Tooltip} from '@primer/react/next'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {
  useBatchComposition,
  useIsFixWithCopilotAvailable,
  useUnifiedBatchActions,
  useUnifiedBatchCount,
} from '../../stores/layout-selectors'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {restoreFocusOnNextFrame, restorePanelFocusAfterClose} from './panel-focus'
import {MANAGE_BATCH_COMMENTS_HEADING_ID, ManageBatchCommentsList} from './manage-batch/ManageBatchCommentsList'
import {
  ManageBatchCommitFormCommands,
  ManageBatchCommitProvider,
  ManageBatchCommitTab,
  ManageBatchCommitTabPrimaryCta,
} from './manage-batch/ManageBatchCommitTab'
import {
  ManageBatchFixFormCommands,
  ManageBatchFixProvider,
  ManageBatchFixTab,
  ManageBatchFixTabPrimaryCta,
} from './manage-batch/ManageBatchFixTab'
import {ManageBatchDisallowedTabBanner} from './manage-batch/ManageBatchDisallowedTabBanner'
import {useManageBatchTelemetry} from './manage-batch/manage-batch-telemetry'
import styles from './ManageBatchPanel.module.css'

export type ManageBatchTab = 'commit' | 'fix'

const TAB_DEFS: Array<{value: ManageBatchTab; label: string}> = [
  {value: 'commit', label: 'Commit suggestions'},
  {value: 'fix', label: 'Fix with Copilot'},
]

const TAB_VALUES = TAB_DEFS.map(({value}) => value)
const FIX_WITH_COPILOT_UNAVAILABLE_MESSAGE = 'Fix with Copilot isn’t available.'

/**
 * Manage batch panel shell.
 *
 * Hosts the docked-panel surface for the unified batch experience. This file
 * delivers the structural pieces only:
 *  - Tab header (Commit suggestions | Fix with Copilot).
 *  - Shared `Comments(N)` list with per-row remove.
 *  - Footer slot framework (Discard batch / Cancel / per-tab primary CTA slot).
 *  - Empty state when the batch is empty.
 *
 * The tab-body content slots are wired by the tab-content follow-ups:
 *  - Commit suggestions tab content
 *  - Fix with Copilot tab content
 *  - Disallowed-tab warning banner
 *  - Outdated suggestion handling
 *
 * Gated behind `unified_batch_pr_comments`. Registered in `panel-registry.tsx`
 * so it joins the enabled-panel set (and the panel-selector dropdown) when
 * the flag is on. Mounted by `DockedSidePanelContent` via `panel-render-map`
 * when the panel is active and the viewport is non-compact; falls back to a
 * Dialog in compact mode.
 */
export const ManageBatchPanel = memo(function ManageBatchPanelImpl() {
  const isFlagEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const isCompact = useIsDockedPanelCompact()
  const {clearBatch} = useUnifiedBatchActions()
  const batchComposition = useBatchComposition()
  const isFixWithCopilotAvailable = useIsFixWithCopilotAvailable()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const tabsHeaderRef = useRef<HTMLDivElement>(null)
  const {onTabSelected} = useManageBatchTelemetry()
  const shouldDefaultToCommit = !isFixWithCopilotAvailable || batchComposition === 'suggestion-only'
  const [activeTab, setActiveTab] = useState<ManageBatchTab>(() => (shouldDefaultToCommit ? 'commit' : 'fix'))

  const handleSwitchToFixWithCopilot = useCallback(() => {
    if (!isFixWithCopilotAvailable) return

    onTabSelected('fix', 'disallowed_tab_nudge')
    setActiveTab('fix')
    restoreFocusOnNextFrame(tabsHeaderRef.current?.querySelector<HTMLElement>('#manage-batch-tab-fix'))
  }, [isFixWithCopilotAvailable, onTabSelected])

  // Success handler used by the Commit tab: clear the batch and close the panel.
  // (The Commit mutation also clears + reloads first so the committed diff shows;
  // the clear here is idempotent.) The Fix with Copilot tab does NOT use this — it
  // clears the batch in its own mutation's `onSuccess` and deliberately keeps the
  // panel open so the returned `taskUrl` can surface the "View agent session" CTA.
  const handleSubmitted = useCallback(() => {
    clearBatch()
    closeManageBatchPanel()
  }, [clearBatch, closeManageBatchPanel])

  if (!isFlagEnabled || !isDockedPanelOpen) return null

  if (!isCompact) {
    return (
      <ActiveTabProvider onSubmitted={handleSubmitted}>
        <Panel panelName={DockedPanel.ManageBatchReviewComments}>
          <Panel.Header>
            <ManageBatchTabsHeader
              activeTab={activeTab}
              isFixWithCopilotAvailable={isFixWithCopilotAvailable}
              setActiveTab={setActiveTab}
              tabsHeaderRef={tabsHeaderRef}
            />
          </Panel.Header>
          <Panel.Body scrollable={false} className={styles.bodyLayout}>
            <ManageBatchBody
              activeTab={activeTab}
              isFixWithCopilotAvailable={isFixWithCopilotAvailable}
              onSwitchToFixWithCopilot={handleSwitchToFixWithCopilot}
            />
          </Panel.Body>
        </Panel>
      </ActiveTabProvider>
    )
  }

  if (!updatedPullRequestHeader) {
    return (
      <ActiveTabProvider onSubmitted={handleSubmitted}>
        <Dialog
          initialFocusRef={closeButtonRef}
          position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
          returnFocusRef={returnFocusRef}
          onClose={closeManageBatchPanel}
          renderHeader={({dialogLabelId}) => (
            <Dialog.Header>
              <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
                <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.ManageBatchReviewComments)}</Dialog.Title>
                <IconButton
                  ref={closeButtonRef}
                  aria-label="Close manage batch panel"
                  icon={XIcon}
                  variant="invisible"
                  onClick={closeManageBatchPanel}
                />
              </div>
              <ManageBatchTabsHeader
                activeTab={activeTab}
                isFixWithCopilotAvailable={isFixWithCopilotAvailable}
                setActiveTab={setActiveTab}
                tabsHeaderRef={tabsHeaderRef}
              />
            </Dialog.Header>
          )}
        >
          <div className={`flex-1 overflow-hidden ${styles.bodyLayout}`}>
            <ManageBatchBody
              activeTab={activeTab}
              isFixWithCopilotAvailable={isFixWithCopilotAvailable}
              onSwitchToFixWithCopilot={handleSwitchToFixWithCopilot}
            />
          </div>
        </Dialog>
      </ActiveTabProvider>
    )
  }

  return (
    <ActiveTabProvider onSubmitted={handleSubmitted}>
      <CompactPanel.Header>
        <ManageBatchTabsHeader
          activeTab={activeTab}
          isFixWithCopilotAvailable={isFixWithCopilotAvailable}
          setActiveTab={setActiveTab}
          tabsHeaderRef={tabsHeaderRef}
        />
      </CompactPanel.Header>
      <CompactPanel.Content>
        <ManageBatchBody
          activeTab={activeTab}
          isFixWithCopilotAvailable={isFixWithCopilotAvailable}
          onSwitchToFixWithCopilot={handleSwitchToFixWithCopilot}
        />
      </CompactPanel.Content>
    </ActiveTabProvider>
  )
})

/**
 * Per-tab context wrapper. Each tab's body and primary CTA render in separate
 * parts of the panel's top section — the tab body and the actions row — so
 * they can't share component-local state. We wrap the entire panel in both
 * tabs' providers so each tab can read and update its own form state and
 * submit callback. Both are mounted unconditionally (rather than only while
 * their own tab is active) so the user's edited commit message/description and
 * the Fix tab's state survive switching tabs and back. Each tab consumes only
 * its own context, so keeping both mounted is harmless.
 */
function ActiveTabProvider({onSubmitted, children}: {onSubmitted: () => void; children: React.ReactNode}) {
  return (
    <ManageBatchCommitProvider onSubmitted={onSubmitted}>
      <ManageBatchFixProvider onSubmitted={onSubmitted}>{children}</ManageBatchFixProvider>
    </ManageBatchCommitProvider>
  )
}

function ManageBatchTabsHeader({
  activeTab,
  isFixWithCopilotAvailable,
  setActiveTab,
  tabsHeaderRef,
}: {
  activeTab: ManageBatchTab
  isFixWithCopilotAvailable: boolean
  setActiveTab: (tab: ManageBatchTab) => void
  tabsHeaderRef: RefObject<HTMLDivElement | null>
}) {
  const {onTabSelected} = useManageBatchTelemetry()

  const handleChange = useCallback(
    (selectedIndex: number) => {
      const next = TAB_VALUES[selectedIndex] ?? 'commit'
      if (next === 'fix' && !isFixWithCopilotAvailable) return

      setActiveTab(next)
      onTabSelected(next)
    },
    [isFixWithCopilotAvailable, setActiveTab, onTabSelected],
  )

  return (
    <div ref={tabsHeaderRef}>
      <Tooltip text={FIX_WITH_COPILOT_UNAVAILABLE_MESSAGE} type="description" direction="n">
        <ManageBatchSegmentedControl
          activeTab={activeTab}
          isFixWithCopilotAvailable={isFixWithCopilotAvailable}
          onChange={handleChange}
        />
      </Tooltip>
    </div>
  )
}

type ManageBatchSegmentedControlProps = {
  activeTab: ManageBatchTab
  isFixWithCopilotAvailable: boolean
  onChange: (selectedIndex: number) => void
  ref?: Ref<HTMLButtonElement>
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'>

function ManageBatchSegmentedControl({
  activeTab,
  isFixWithCopilotAvailable,
  onChange,
  ref: forwardedRef,
  ...tooltipTriggerProps
}: ManageBatchSegmentedControlProps) {
  return (
    <SegmentedControl aria-label="Manage batch action mode" fullWidth onChange={onChange} className={styles.tabs}>
      {TAB_DEFS.map(({value, label}) => {
        const fixUnavailable = value === 'fix' && !isFixWithCopilotAvailable
        const fixButtonRefProps = value === 'fix' ? {ref: forwardedRef} : {}

        return (
          <SegmentedControl.Button
            key={value}
            {...fixButtonRefProps}
            {...(fixUnavailable ? tooltipTriggerProps : {})}
            id={`manage-batch-tab-${value}`}
            selected={activeTab === value}
            disabled={fixUnavailable}
            data-testid={`manage-batch-tab-${value}`}
          >
            {label}
          </SegmentedControl.Button>
        )
      })}
    </SegmentedControl>
  )
}

function ManageBatchBody({
  activeTab,
  isFixWithCopilotAvailable,
  onSwitchToFixWithCopilot,
}: {
  activeTab: ManageBatchTab
  isFixWithCopilotAvailable: boolean
  onSwitchToFixWithCopilot: () => void
}) {
  const count = useUnifiedBatchCount()

  // The top section holds the active tab's form fields and the actions row
  // (Discard / Cancel / primary CTA). For each tab we wrap it in the tab's
  // `*FormCommands` so ⌘/Ctrl+Enter submits the batch from anywhere in the
  // form area — mirroring `ApplySuggestionDialog`.
  const topSection = (
    <div className={styles.panelTop}>
      <div data-testid={`manage-batch-tab-body-${activeTab}`}>
        {activeTab === 'commit' && (
          <>
            <ManageBatchDisallowedTabBanner
              onSwitchToFixWithCopilot={isFixWithCopilotAvailable ? onSwitchToFixWithCopilot : undefined}
            />
            <ManageBatchCommitTab />
          </>
        )}
        {activeTab === 'fix' && isFixWithCopilotAvailable && <ManageBatchFixTab />}
      </div>
      <ManageBatchActions activeTab={activeTab} />
    </div>
  )

  return (
    <>
      {activeTab === 'commit' && <ManageBatchCommitFormCommands>{topSection}</ManageBatchCommitFormCommands>}
      {activeTab === 'fix' &&
        (isFixWithCopilotAvailable ? (
          <ManageBatchFixFormCommands>{topSection}</ManageBatchFixFormCommands>
        ) : (
          topSection
        ))}
      <div className={styles.panelScrollable}>
        <h3 className={styles.commentsHeading} id={MANAGE_BATCH_COMMENTS_HEADING_ID} tabIndex={-1}>
          Batched comments <CounterLabel className="ml-1">{count}</CounterLabel>
        </h3>
        <ManageBatchCommentsList />
      </div>
    </>
  )
}

function ManageBatchActions({activeTab}: {activeTab: ManageBatchTab}) {
  const isFixWithCopilotAvailable = useIsFixWithCopilotAvailable()
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const {clearBatch} = useUnifiedBatchActions()
  const {onPanelClose} = useManageBatchTelemetry()

  const handleDiscard = useCallback(() => {
    onPanelClose()
    closeManageBatchPanel()
    clearBatch()
  }, [clearBatch, closeManageBatchPanel, onPanelClose])

  const handleCancel = useCallback(() => {
    onPanelClose()
    closeManageBatchPanel()
  }, [closeManageBatchPanel, onPanelClose])

  return (
    <div className={styles.actions}>
      <Button variant="danger" onClick={handleDiscard}>
        Discard batch
      </Button>
      <div className={styles.actionsRight}>
        <Button variant="default" onClick={handleCancel}>
          Cancel
        </Button>
        <div data-testid={`manage-batch-primary-cta-slot-${activeTab}`}>
          {activeTab === 'commit' && <ManageBatchCommitTabPrimaryCta />}
          {activeTab === 'fix' && isFixWithCopilotAvailable && <ManageBatchFixTabPrimaryCta />}
        </div>
      </div>
    </div>
  )
}

function useCloseManageBatchPanel() {
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  return useCallback(() => {
    restorePanelFocusAfterClose(returnFocusRef.current)
    closePanel()
  }, [closePanel, returnFocusRef])
}
